import { COLS, SLOTS } from './deck';
import type {
  BonusGroup,
  CardValue,
  ColumnResult,
  GameState,
  HoleResult,
  RuleOptions,
  ScoreBreakdown,
  Slot,
} from './types';

export type ScoringOptions = Pick<RuleOptions, 'bonusRequiresAdjacent' | 'multipleBonusGroups'>;

const DEFAULT_SCORING: ScoringOptions = { bonusRequiresAdjacent: false, multipleBonusGroups: true };

/** Flat bonus by matched-column count (RULES.md 8.2). */
export const BONUS_BY_K: Readonly<Record<number, number>> = { 2: -10, 3: -15, 4: -20 };

export function bonusFor(k: number): number {
  return BONUS_BY_K[k] ?? 0;
}

/** Score of a matched column of value v: 0, except -5 pairs score -10. */
export function pairScore(v: CardValue): number {
  return v === -5 ? -10 : 0;
}

/**
 * Score per-slot values (null = not counted). This is the core of scoreGrid.
 * `values` is row-major, length 8.
 */
export function scoreValues(values: readonly (CardValue | null)[], options?: Partial<ScoringOptions>): ScoreBreakdown {
  const opts = { ...DEFAULT_SCORING, ...options };
  if (values.length !== SLOTS) throw new Error(`grid must have ${SLOTS} slots`);
  const columns: ColumnResult[] = [];
  const matched: { column: number; value: CardValue }[] = [];
  let counted = 0;
  for (let c = 0; c < COLS; c++) {
    const top = values[c] ?? null;
    const bottom = values[c + COLS] ?? null;
    if (top !== null) counted++;
    if (bottom !== null) counted++;
    let res: ColumnResult;
    if (top !== null && bottom !== null) {
      if (top === bottom) {
        res = { column: c, top, bottom, kind: top === -5 ? 'minusFivePair' : 'pair', score: pairScore(top) };
        matched.push({ column: c, value: top });
      } else {
        res = { column: c, top, bottom, kind: 'unmatched', score: top + bottom };
      }
    } else if (top !== null || bottom !== null) {
      res = { column: c, top, bottom, kind: 'partial', score: (top ?? 0) + (bottom ?? 0) };
    } else {
      res = { column: c, top: null, bottom: null, kind: 'hidden', score: 0 };
    }
    columns.push(res);
  }

  // Group matched columns by value (and by adjacency when required).
  const groups: { value: CardValue; columns: number[] }[] = [];
  for (const m of matched) {
    const last = [...groups].reverse().find((g) => g.value === m.value);
    const lastCol = last ? (last.columns[last.columns.length - 1] as number) : -99;
    if (last && (!opts.bonusRequiresAdjacent || lastCol === m.column - 1)) last.columns.push(m.column);
    else groups.push({ value: m.value, columns: [m.column] });
  }
  let bonuses: BonusGroup[] = groups
    .filter((g) => g.columns.length >= 2)
    .map((g) => ({ value: g.value, k: g.columns.length, columns: g.columns, bonus: bonusFor(g.columns.length) }));
  if (!opts.multipleBonusGroups && bonuses.length > 1) {
    const best = bonuses.reduce((a, b) => (b.bonus < a.bonus ? b : a));
    bonuses = [best];
  }

  const columnTotal = columns.reduce((a, c) => a + c.score, 0);
  const bonusTotal = bonuses.reduce((a, b) => a + b.bonus, 0);
  return { total: columnTotal + bonusTotal, columns, bonuses, columnTotal, bonusTotal, counted };
}

/**
 * Score a grid with a full breakdown for the UI.
 * - revealAll (default true): count every card whose value is known (face-down
 *   included). Use on the authoritative state or once a hole is over.
 * - revealAll false: count only face-up cards (same as projectedScore).
 * Redacted face-down slots (card null) are never counted.
 */
export function scoreGrid(
  grid: readonly Slot[],
  opts?: { revealAll?: boolean; options?: Partial<ScoringOptions> },
): ScoreBreakdown {
  const revealAll = opts?.revealAll ?? true;
  const values = grid.map((s) => (s.card && (revealAll || s.faceUp) ? s.card.value : null));
  return scoreValues(values, opts?.options);
}

/** Live-scoreboard score: face-up cards only, pairs and bonuses among fully face-up columns. */
export function projectedScore(grid: readonly Slot[], options?: Partial<ScoringOptions>): number {
  if (grid.length === 0) return 0;
  return scoreGrid(grid, { revealAll: false, ...(options ? { options } : {}) }).total;
}

/** Regulation totals (playoff holes do not count). */
export function totals(state: GameState): number[] {
  return state.players.map((p) => p.holeScores.reduce((a, b) => a + b, 0));
}

/** Every completed hole in order, regulation then playoff. */
export function holeResults(state: GameState): HoleResult[] {
  const out: HoleResult[] = [];
  const n = state.players.length;
  const running = new Array<number>(n).fill(0);
  const regulation = Math.max(0, ...state.players.map((p) => p.holeScores.length));
  const best = (scores: (number | null)[]): number[] => {
    const nums = scores.filter((x): x is number => x !== null);
    if (nums.length === 0) return [];
    const low = Math.min(...nums);
    return scores.flatMap((x, i) => (x === low ? [i] : []));
  };
  for (let h = 0; h < regulation; h++) {
    const scores = state.players.map((p) => p.holeScores[h] ?? null);
    scores.forEach((x, i) => {
      running[i] = (running[i] as number) + (x ?? 0);
    });
    out.push({ hole: h + 1, playoff: false, scores, totals: running.slice(), best: best(scores) });
  }
  const playoffs = Math.max(0, ...state.players.map((p) => p.playoffScores.length));
  for (let h = 0; h < playoffs; h++) {
    const scores = state.players.map((p) => p.playoffScores[h] ?? null);
    out.push({ hole: regulation + h + 1, playoff: true, scores, totals: running.slice(), best: best(scores) });
  }
  return out;
}
